/**
 * Compact banks panel — embedded inside PropertyDetails.
 * Shows bank accounts linked to the current property + quick add CTA.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Landmark, Plus, Star, Smartphone, ExternalLink } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { useNavigate } from 'react-router-dom';
import { toast } from '../../hooks/use-toast';
import BankFormModal from './BankFormModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PropertyBanksSection = ({ propertyId, propertyName, token }) => {
  const navigate = useNavigate();
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchBanks = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/banks?property_id=${propertyId}`, { headers });
      setBanks(res.data.banks || []);
    } catch (e) {
      // Silent fail — banks panel is optional
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId, token]);

  useEffect(() => { fetchBanks(); }, [fetchBanks]);

  const setPrimary = async (bankId) => {
    try {
      await axios.post(`${API}/banks/${bankId}/set-primary`, {}, { headers });
      toast({ title: 'Marked as primary' });
      fetchBanks();
    } catch (e) {
      toast({ title: 'Failed', description: e.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  return (
    <Card className="p-5 mb-6 border border-gray-200" data-testid="property-banks-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Landmark size={18} className="text-emerald-600" />
          <h3 className="text-lg font-bold text-gray-900">Banks &amp; UPI</h3>
          <span className="text-xs text-gray-500">({banks.length})</span>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate('/manager/money/banks')}
            className="text-xs text-emerald-700 hover:underline inline-flex items-center gap-1"
            data-testid="property-banks-view-all"
          >
            View all <ExternalLink size={11} />
          </button>
          <Button
            size="sm"
            onClick={() => { setEditing(null); setShowForm(true); }}
            className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-7"
            data-testid="property-bank-add-btn"
          >
            <Plus size={12} className="mr-1" /> Add
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : banks.length === 0 ? (
        <p className="text-sm text-gray-500">
          No bank accounts linked to this property yet. Add one to print on rent invoices.
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {banks.map(b => (
            <div key={b.id} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 mb-1">
                    <p className="font-semibold text-sm text-gray-900 truncate">{b.bank_name}</p>
                    {b.is_primary_for_property && (
                      <span className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                        <Star size={9} /> PRIMARY
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-gray-500 mb-1">{b.holder_name}</p>
                  <p className="text-xs font-mono text-gray-700 tabular-nums">{b.account_number_masked}</p>
                  <p className="text-[10px] text-gray-500 font-mono">IFSC {b.ifsc}</p>
                  {b.upi_vpa && (
                    <p className="text-[10px] text-indigo-700 font-mono inline-flex items-center gap-1 mt-0.5">
                      <Smartphone size={9} /> {b.upi_vpa}
                    </p>
                  )}
                </div>
                {!b.is_primary_for_property && (
                  <button
                    onClick={() => setPrimary(b.id)}
                    title="Set as primary"
                    className="p-1 text-amber-600 hover:bg-amber-50 rounded shrink-0"
                    data-testid={`property-bank-primary-${b.id}`}
                  >
                    <Star size={12} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <BankFormModal
          open={showForm}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); fetchBanks(); }}
          properties={propertyId ? [{ id: propertyId, name: propertyName }] : []}
          token={token}
          editing={editing}
          defaultPropertyId={propertyId}
        />
      )}
    </Card>
  );
};

export default PropertyBanksSection;
